import { useCallback } from 'react'

import { useToast } from '@circlefin/components'
import { useForm } from '@circlefin/form'
import * as y from 'yup'

import { SIGNING_METHODS, DEFAULT_CLASSES } from '../constants'

import type {
  GeneratedPayload,
  SigningMethod,
} from '@features/cpn.hooks/useWalletValidator'

// Component-specific types
interface SignatureForm {
  signature: string
}

interface SignatureValidationProps {
  generatedPayload: GeneratedPayload
  onSignatureSubmit: (data: SignatureForm) => void
  onBack: () => void
  isValidating: boolean
}

/**
 * Get appropriate placeholder text for signature input based on method.
 */
const getSignaturePlaceholder = (method: SigningMethod) => {
  if (method === 'eip712') {
    return 'Paste your off-chain signature (0x...) here...'
  }
  if (method === 'eip1559') {
    return 'Paste your signed transaction (0x...) here...'
  }
  return 'Paste your base58 signature (87-88 characters) here...'
}

/**
 * Copy text to clipboard with error handling.
 */
const copyToClipboard = async (text: string): Promise<void> => {
  await navigator.clipboard.writeText(text)
}

/**
 * Format error message for display.
 */
const formatErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  return typeof error === 'string' ? error : 'Unknown error'
}

// Form validation schema
const signatureSchema = y.object({
  signature: y.string().required('Please enter the signature'),
})

/**
 * Signature validation component for the third step of wallet compatibility testing
 * Displays the generated payload and collects the user's signature for validation.
 */
export const SignatureValidation: React.FC<SignatureValidationProps> = ({
  generatedPayload,
  onSignatureSubmit,
  onBack,
  isValidating,
}) => {
  const toast = useToast()

  // Signature Form
  const [SignatureForm] = useForm<SignatureForm>({
    schema: signatureSchema,
  })

  // Get method configuration for display
  const methodConfig = SIGNING_METHODS.find(
    (m) => m.value === generatedPayload.method,
  )

  // Handle signature validation
  const handleSignatureSubmit = useCallback(
    (data: SignatureForm) => {
      onSignatureSubmit(data)
    },
    [onSignatureSubmit],
  )

  // Handle copy to clipboard
  const handleCopyPayload = useCallback(async () => {
    try {
      await copyToClipboard(generatedPayload.formattedPayload)
      toast.success('Copied to clipboard!')
    } catch (error) {
      // Note: useToast might not have an error method, handle gracefully
      console.warn('Failed to copy to clipboard:', formatErrorMessage(error))
    }
  }, [generatedPayload.formattedPayload, toast])

  // Wrapper for onClick to handle async
  const handleCopyClick = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleCopyPayload()
  }, [handleCopyPayload])

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-2 text-neutral-strong type-h-title-md">
          Sign Payload & Validate
        </h4>
        <p className="mb-4 text-neutral-subtle type-body-sm">
          Copy the payload below, sign it with your wallet using the method
          below, then paste the signature.
        </p>
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
          <div className="text-sm text-blue-800">
            <strong>📝 Signing Instructions:</strong>{' '}
            {methodConfig?.signingMethod}
          </div>
        </div>
      </div>

      {/* Generated Payload Display */}
      <div
        className="rounded-md border border-neutral-subtle bg-neutral-subtle p-4"
        data-testid="payload-container"
      >
        <div className="mb-2 flex items-center justify-between">
          <h5 className="text-neutral-strong type-body-base-bold">
            Generated Payload
          </h5>
          <button
            className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
            data-testid="copy-button"
            onClick={handleCopyClick}
            type="button"
          >
            Copy
          </button>
        </div>
        <pre
          className="max-h-64 overflow-auto text-xs text-neutral-strong"
          data-testid="payload-content"
        >
          {generatedPayload.formattedPayload}
        </pre>
      </div>

      <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
        <div className="flex">
          <div className="text-sm text-yellow-800">
            <strong>⚠️ Security Notice:</strong> Always carefully review the
            payload before signing. Verify all addresses, amounts, and
            transaction details match your expectations. It contains zero
            amounts, expired timestamps, and burn addresses - it cannot execute
            any real transactions.
          </div>
        </div>
      </div>

      <SignatureForm
        className="space-y-4"
        data-testid="signature-form"
        onSubmit={handleSignatureSubmit}
      >
        <SignatureForm.Textarea
          data-testid="signature-input"
          label="Signature"
          name="signature"
          placeholder={getSignaturePlaceholder(generatedPayload.method)}
          resizable={false}
          required
        />

        <div className="flex space-x-3">
          <SignatureForm.SubmitButton
            className="flex-1 sm:flex-none"
            data-testid="validate-button"
            disabled={isValidating}
            variant="primary"
          >
            {isValidating ? 'Validating...' : 'Validate Signature'}
          </SignatureForm.SubmitButton>
          <button
            className={DEFAULT_CLASSES.button.secondary}
            data-testid="back-button"
            onClick={onBack}
            type="button"
          >
            Back
          </button>
        </div>
      </SignatureForm>
    </div>
  )
}
